import React, { useState } from 'react';
import { GlassModal, GlassButton, GlassInput } from '../ui';
import { AlertTriangle, Sparkles } from 'lucide-react';
import {
    VoiceRule,
    VoiceRuleCandidate,
    createVoiceRule,
    suggestVoiceRules,
} from '../../services/api';

const getApiError = (e: any, fallback: string): string => {
    const detail = e?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
    }
    return fallback;
};

interface CandidateState extends VoiceRuleCandidate {
    editing: boolean;
    draftText: string;
    saving: boolean;
    error: string | null;
}

interface Props {
    projectId: string;
    onClose: () => void;
    /** Called once per accepted candidate, immediately after its POST resolves. */
    onAccepted: (rule: VoiceRule) => void;
}

const VoiceRuleSuggestModal: React.FC<Props> = ({ projectId, onClose, onAccepted }) => {
    const [focus, setFocus] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [candidates, setCandidates] = useState<CandidateState[] | null>(null);

    const runSuggest = async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const result = await suggestVoiceRules(projectId, { focus: focus.trim() || undefined });
            setCandidates(
                result.candidates.map((c) => ({
                    rule_text: c.rule_text,
                    editing: false,
                    draftText: c.rule_text,
                    saving: false,
                    error: null,
                })),
            );
        } catch (err) {
            setLoadError(getApiError(err, 'AI suggestion failed — please try again'));
        } finally {
            setLoading(false);
        }
    };

    const updateCandidate = (index: number, patch: Partial<CandidateState>) => {
        setCandidates((prev) => {
            if (!prev) return prev;
            const next = [...prev];
            next[index] = { ...next[index], ...patch };
            return next;
        });
    };

    const discardCandidate = (index: number) => {
        setCandidates((prev) => (prev ? prev.filter((_, i) => i !== index) : prev));
    };

    const acceptCandidate = async (index: number) => {
        if (!candidates) return;
        const candidate = candidates[index];
        const text = candidate.draftText.trim();
        if (!text) {
            updateCandidate(index, { error: 'Rule text is required' });
            return;
        }
        updateCandidate(index, { saving: true, error: null });
        try {
            const created = await createVoiceRule(projectId, {
                rule_text: text,
                scope: ['all'],
                source: 'ai_suggested',
            });
            onAccepted(created);
            discardCandidate(index);
        } catch (err) {
            updateCandidate(index, { saving: false, error: getApiError(err, 'Failed to save rule') });
        }
    };

    const hasUnaccepted = !!candidates && candidates.length > 0;

    return (
        <GlassModal isOpen onClose={onClose} title="Suggest rules with AI" size="md">
            {!candidates && (
                <div className="ps-form-group" style={{ margin: 0 }}>
                    <p className="ps-description" style={{ marginBottom: 'var(--space-3)' }}>
                        AIME will draft a handful of voice &amp; tone rules grounded on this project's
                        brand guide and its existing rules. Nothing is saved until you accept a
                        candidate individually. Accepted rules apply to <strong>all modules</strong> by
                        default — edit a rule afterwards to narrow it to specific ones.
                    </p>
                    <GlassInput
                        label="Optional focus (e.g. 'we're a law firm, keep it conservative')"
                        value={focus}
                        onChange={setFocus}
                        placeholder="Anything specific to steer toward?"
                    />
                    {loadError && (
                        <div className="ps-inline-error" style={{ marginTop: 'var(--space-3)' }}>
                            <AlertTriangle size={14} />
                            <span>{loadError}</span>
                        </div>
                    )}
                    <div style={{ marginTop: 'var(--space-4)' }}>
                        <GlassButton
                            variant="primary"
                            icon={<Sparkles size={16} />}
                            loading={loading}
                            disabled={loading}
                            onClick={runSuggest}
                        >
                            Suggest rules with AI
                        </GlassButton>
                    </div>
                </div>
            )}

            {candidates && candidates.length === 0 && (
                <p className="ps-empty-state">
                    No candidates remain — everything was either added or discarded.
                </p>
            )}

            {candidates && candidates.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    {candidates.map((candidate, index) => (
                        <div key={index} className="ps-vt-candidate">
                            {candidate.editing ? (
                                <textarea
                                    className="ps-textarea"
                                    value={candidate.draftText}
                                    onChange={(e) => updateCandidate(index, { draftText: e.target.value })}
                                    rows={2}
                                    maxLength={300}
                                />
                            ) : (
                                <span className="ps-vt-rule-text">{candidate.draftText}</span>
                            )}
                            {candidate.error && (
                                <div className="ps-inline-error">
                                    <AlertTriangle size={14} />
                                    <span>{candidate.error}</span>
                                </div>
                            )}
                            <div className="ps-vt-candidate-actions">
                                <GlassButton
                                    variant="primary"
                                    size="sm"
                                    loading={candidate.saving}
                                    disabled={candidate.saving || !candidate.draftText.trim()}
                                    onClick={() => acceptCandidate(index)}
                                >
                                    Add
                                </GlassButton>
                                {candidate.editing ? (
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        disabled={candidate.saving}
                                        onClick={() => updateCandidate(index, { editing: false })}
                                    >
                                        Done editing
                                    </GlassButton>
                                ) : (
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        disabled={candidate.saving}
                                        onClick={() => updateCandidate(index, { editing: true })}
                                    >
                                        Edit
                                    </GlassButton>
                                )}
                                <GlassButton
                                    variant="secondary"
                                    size="sm"
                                    disabled={candidate.saving}
                                    onClick={() => discardCandidate(index)}
                                >
                                    Discard
                                </GlassButton>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {hasUnaccepted && (
                <p className="ps-vt-modal-warning" style={{ marginTop: 'var(--space-3)' }}>
                    Candidates you haven't added are not saved.
                </p>
            )}

            <div style={{ marginTop: 'var(--space-4)', display: 'flex', justifyContent: 'flex-end' }}>
                <GlassButton variant="secondary" onClick={onClose}>
                    Done
                </GlassButton>
            </div>
        </GlassModal>
    );
};

export default VoiceRuleSuggestModal;
